import { z } from 'zod';

/**
 * Vorlage für den „Grund der Steuerbefreiung" am Verkaufsbeleg (2026-10-15).
 * Tenant-weit kuratiert (Anlegen/Ändern/Löschen mit `settings.update`), im Beleg
 * für alle Angemeldeten auswählbar. Der Beleg übernimmt den Text als Snapshot in
 * der Belegsprache: `textEn` bei englischen Belegen (falls hinterlegt), sonst
 * `textDe`.
 *
 * Längen wie `taxExemptReason` am Beleg (max. 300) — ein längerer Vorlagentext
 * ließe sich gar nicht speichern.
 */
export const TAX_EXEMPT_REASON_MAX_LENGTH = 300;

export const taxExemptReasonTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  textDe: z.string(),
  textEn: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TaxExemptReasonTemplateResponse = z.infer<typeof taxExemptReasonTemplateResponseSchema>;

const nameSchema = z.string().trim().min(1, 'Bitte einen Namen angeben.').max(120);
const textDeSchema = z
  .string()
  .trim()
  .min(1, 'Bitte den deutschen Text angeben.')
  .max(TAX_EXEMPT_REASON_MAX_LENGTH);
/** Leerer englischer Text = keiner (englische Belege bekommen dann den deutschen). */
const textEnSchema = z.string().trim().max(TAX_EXEMPT_REASON_MAX_LENGTH).nullable();
const sortOrderSchema = z.number().int().min(0).max(100_000);

export const createTaxExemptReasonTemplateSchema = z.object({
  name: nameSchema,
  textDe: textDeSchema,
  textEn: textEnSchema.optional(),
  sortOrder: sortOrderSchema.optional(),
});
export type CreateTaxExemptReasonTemplateInput = z.infer<typeof createTaxExemptReasonTemplateSchema>;

export const updateTaxExemptReasonTemplateSchema = z.object({
  name: nameSchema.optional(),
  textDe: textDeSchema.optional(),
  textEn: textEnSchema.optional(),
  sortOrder: sortOrderSchema.optional(),
});
export type UpdateTaxExemptReasonTemplateInput = z.infer<typeof updateTaxExemptReasonTemplateSchema>;

/**
 * Text einer Vorlage in der Belegsprache: Englisch nur, wenn ein englischer Text
 * hinterlegt ist — sonst der deutsche (Pflichtfeld).
 */
export function taxExemptReasonTemplateText(
  template: Pick<TaxExemptReasonTemplateResponse, 'textDe' | 'textEn'>,
  language: string | null | undefined,
): string {
  return language?.toUpperCase() === 'EN' && template.textEn ? template.textEn : template.textDe;
}
